import {
  Card,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  InactiveIcon,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
  useWindowWidth,
} from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Clock } from '@/components/Clock';
import { EmptyStateErrorUnexpected } from '@/components/EmptyState';
import { MOBILE_WIDTH } from '@/constants';
import { BroadcastScheduleProvider } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { useTwoCollapsableCardsState } from '@/hooks/useTwoCollapsableCardsState';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useChannelRecordingTasks } from '../api/get-channel-recording-tasks';
import { usePostChannelRecordingTask } from '../api/post-channel-recording-task';
import { useUpdateChannelRecordingTaskAction } from '../api/update-channel-recording-task-action';
import { useUploadChannelRecordingTaskFile } from '../api/upload-channel-recording-task-file';
import { LastRecordingFilePreviewModal } from '../components/LastRecordingFilePreviewModal/LastRecordingFilePreviewModal';
import { useChannelRecordingTaskFilters } from '../components/RecorderDetailCard/channel-recording-task-filters';
import { RecorderDrawerForm } from '../components/RecorderDrawerForm';
import { RecorderTable } from '../components/RecorderTable';
import { useChannelRecordingsPermissions } from '../hooks/useChannelRecordingsPermissions';
import { useRecorderControllers } from '../hooks/useRecorderControllers';
import { ChannelRecordingTaskActionEnum, ChannelRecordingTaskType } from '../types';

export default function RecorderPage() {
  const { t } = useTranslation();
  const { width } = useWindowWidth();
  const { recorderDrawer } = useRecorderControllers();
  const { filters, paginationValues, clearFilters } = useChannelRecordingTaskFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: channelRecordingTasks = [], total = 0, aggregation } = {},
    isLoading,
    isError,
  } = useChannelRecordingTasks({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
    paginationValues,
    config: {
      refetchInterval: 60_000,
    },
  });
  const { mutate: createChannelRecordingTask, isPending: isCreatingChannelRecordingTask } =
    usePostChannelRecordingTask();
  const { mutate: uploadChannelRecordingTaskFile, isPending: isUploadingChannelRecordingTask } =
    useUploadChannelRecordingTaskFile();

  const {
    data,
    isFirstCardCollapsed,
    isOpen,
    handleOpenSecondCard,
    handleFirstCardCollapse,
    handleAccordionOpen,
  } = useTwoCollapsableCardsState<any>();

  const recordingFilePreviewDialog = useDisclosureWithData<ChannelRecordingTaskType>();
  const cancelChannelRecordingTaskDialog = useDisclosureWithData<ChannelRecordingTaskType>();

  const { mutate: updateChannelRecordingTaskAction } = useUpdateChannelRecordingTaskAction();

  const isMobile = width < MOBILE_WIDTH;

  const { canCreate, canDuplicate, canCancel } = useChannelRecordingsPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    recorderDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onDuplicate = (channelRecordingTask: ChannelRecordingTaskType) => {
    if (!canDuplicate) return;
    recorderDrawer.open({ type: FormTypeEnum.DUPLICATE, data: channelRecordingTask });
  };

  const onCancel = (channelRecordingTask: ChannelRecordingTaskType) => {
    if (!canCancel) return;
    cancelChannelRecordingTaskDialog.open(channelRecordingTask);
  };

  return (
    <BroadcastScheduleProvider>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Grabaciones')}
          </Typography>

          <div className="flex items-center gap-4">
            <Clock />

            {canCreate && (
              <IconButtonSection
                className="mr-size-16"
                onClick={onCreate}
                internalProps={{
                  icon: {
                    size: IconSizeEnum.SM,
                    type: PlusIcon,
                  },
                  text: {
                    value: t('Nueva'),
                    color: FontColorEnum.Default,
                  },
                }}
              />
            )}
          </div>
        </div>

        {isError ? (
          <EmptyStateErrorUnexpected />
        ) : (
          <div className="main flex min-h-full w-full flex-wrap gap-size-24">
            <div
              className={clsx(
                'column-1 flex h-auto flex-col items-start gap-size-8',
                !isFirstCardCollapsed && 'flex-grow'
              )}
            >
              <Card
                isAccordion={isMobile}
                lineSeparator={isOpen}
                isCollapsable={data !== null && !isMobile}
                isCollapsed={isFirstCardCollapsed && !isMobile}
                onCollapse={handleFirstCardCollapse}
                isOpen={isOpen}
                onOpen={handleAccordionOpen}
                classes={{
                  header: clsx(!isMobile && 'py-3'),
                }}
              >
                <RecorderTable
                  items={channelRecordingTasks}
                  totalItems={total}
                  aggregation={aggregation}
                  activeItem={data}
                  isFetching={isLoading}
                  onDuplicate={onDuplicate}
                  onCancel={onCancel}
                  onSelect={handleOpenSecondCard}
                  onPreview={recordingFilePreviewDialog.open}
                />
              </Card>
            </div>
          </div>
        )}
      </div>

      <RecorderDrawerForm
        isOpen={recorderDrawer.isOpen}
        onClose={recorderDrawer.close}
        onSubmit={data => {
          if (data.file) {
            uploadChannelRecordingTaskFile(
              {
                ...data,
                file: data.file,
                start_time: new Date(data.start_datetime).toISOString(),
                end_time: new Date(data.end_datetime).toISOString(),
              },
              {
                onSuccess: () => {
                  recorderDrawer.close();
                },
              }
            );
            return;
          }

          createChannelRecordingTask(data, {
            onSuccess: () => {
              recorderDrawer.close();
            },
          });
        }}
        isMutating={isCreatingChannelRecordingTask || isUploadingChannelRecordingTask}
        data={recorderDrawer.data}
      />

      <ModalDialog
        isOpen={cancelChannelRecordingTaskDialog.isOpen}
        onClose={cancelChannelRecordingTaskDialog.close}
        onAction={() => {
          if (cancelChannelRecordingTaskDialog.data) {
            updateChannelRecordingTaskAction({
              id: cancelChannelRecordingTaskDialog.data.id,
              action: ChannelRecordingTaskActionEnum.REMOVE,
            });
          }
        }}
        heading={t('Cancelar proceso de grabación')}
        actionLabel={t('Cancelar grabación')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro que quieres cancelar el proceso de grabación?')}
          description={t(
            'No se ejecutará la grabación. Si en un futuro quieres volver a activar el proceso tendrás que crear una nueva programación de grabación.'
          )}
          icon={InactiveIcon}
        />
      </ModalDialog>

      <LastRecordingFilePreviewModal
        isOpen={recordingFilePreviewDialog.isOpen}
        onClose={recordingFilePreviewDialog.close}
        channelRecordingTask={recordingFilePreviewDialog.data}
      />
    </BroadcastScheduleProvider>
  );
}
